"use client";

import { c as _c } from "react/compiler-runtime";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import WaveDropsAll from "@/components/waves/drops/wave-drops-all";
import MobileMemesArtSubmissionBtn from "@/components/waves/memes/submission/MobileMemesArtSubmissionBtn";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeFeedRoute } from "@/helpers/navigation.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWave } from "@/hooks/useWave";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
}
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(36);
  const {
    wave,
    firstUnreadSerialNo
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const containerRef = useRef(null);
  const [initialDropState, setInitialDropState] = useState(null);
  const {
    isMemesWave
  } = useWave(wave);
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const [activeDrop, setActiveDrop] = useState(null);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  const dividerTarget = initialDropState?.waveId === wave.id ? initialDropState.dividerSerialNo : firstUnreadSerialNo;
  let t1;
  let t2;
  if ($[0] !== firstUnreadSerialNo || $[1] !== pathname || $[2] !== router || $[3] !== searchParams || $[4] !== wave.id) {
    t1 = () => {
      const dropParam = searchParams?.get("serialNo");
      if (!dropParam) {
        return;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        return;
      }
      const dividerParam = searchParams?.get("divider");
      const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
      const dividerSerialNo = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
      setInitialDropState({
        waveId: wave.id,
        serialNo: parsed,
        dividerSerialNo
      });
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeFeedRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t2 = [searchParams, router, pathname, wave.id, firstUnreadSerialNo];
    $[0] = firstUnreadSerialNo;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = wave.id;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  const {
    waveViewStyle
  } = useLayout();
  let t3;
  if ($[7] !== waveViewStyle) {
    t3 = waveViewStyle || {};
    $[7] = waveViewStyle;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const containerStyle = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setActiveDrop(null);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== wave) {
    t5 = [wave];
    $[10] = wave;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (drop, partId) => {
      setActiveDrop({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const onReply = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (drop_0, partId_0) => {
      setActiveDrop({
        action: ActiveDropAction.QUOTE,
        drop: drop_0,
        partId: partId_0
      });
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onQuote = t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = t9 => {
      const {
        drop: drop_1,
        partId: partId_1
      } = t9;
      onReply(drop_1, partId_1);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleReply = t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = t10 => {
      const {
        drop: drop_2,
        partId: partId_2
      } = t10;
      onQuote(drop_2, partId_2);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleQuote = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setActiveDrop(null);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const onCancelReplyQuote = t10;
  const t11 = wave.metrics?.muted ?? false;
  let t12;
  if ($[17] !== activeDrop || $[18] !== dividerTarget || $[19] !== scrollTarget || $[20] !== t11 || $[21] !== wave.id) {
    t12 = <WaveDropsAll key={wave.id} waveId={wave.id} onReply={handleReply} onQuote={handleQuote} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} dropId={null} isMuted={t11} />;
    $[17] = activeDrop;
    $[18] = dividerTarget;
    $[19] = scrollTarget;
    $[20] = t11;
    $[21] = wave.id;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== activeDrop || $[24] !== editingDropId || $[25] !== isApp || $[26] !== wave) {
    t13 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={DropMode.BOTH} /></CreateDropWaveWrapper></div>;
    $[23] = activeDrop;
    $[24] = editingDropId;
    $[25] = isApp;
    $[26] = wave;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== isMemesWave || $[29] !== wave) {
    t14 = isMemesWave && <MobileMemesArtSubmissionBtn wave={wave} />;
    $[28] = isMemesWave;
    $[29] = wave;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== containerStyle || $[32] !== t12 || $[33] !== t13 || $[34] !== t14) {
    t15 = <div ref={containerRef} className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow" style={containerStyle}>{t12}{t13}{t14}</div>;
    $[31] = containerStyle;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
export default MyStreamWaveChat;